/**
 * NOT a client module (FRIDGE-929, measured 09/17/2026).
 *
 * This wrapper used to carry "use client", which made `seed` — the full
 * RecommendedProduct rows from getPopularProducts(), `specs_json` and its
 * multi-kilobyte `description` included — a CLIENT PROP. Next serialises
 * client props into the React Server Components flight payload of every page
 * that mounts the rail, so /brands/lg shipped 12 seed rows (~10.5 KB of props
 * plus ~18 KB of description text rows) that the carousel never renders: it
 * reads brand, title, price, one image, href, rating, reviews and the badge
 * signals, nothing else. The same rail is mounted on every /local, /rebates,
 * /brands and blog page.
 *
 * As a server component the mapping to CarouselProduct runs here, on the
 * server, and only the mapped pool crosses to the client
 * (`toRecentlyViewedPool`, pinned by tests/recently-viewed-pool-projection
 * .test.ts). The rendered markup is unchanged: RecentlyViewedCarousel is still
 * the client component, still localStorage-gated, and still receives the same
 * `pool` values it computed for itself before.
 *
 * The one client importer (app/operations/design/page-client.tsx) keeps
 * working: a component without server-only imports simply runs on the client
 * when imported from a client module.
 */
import { RecentlyViewedCarousel, type RecentlyViewedItem } from "./recently-viewed-carousel"
import type { RecommendedProduct } from "@/lib/recommendations"
import type { CarouselProduct } from "./product-carousel-card"
import { badgesFromRecommended } from "@/components/badges/from-product"
import { formatPrice } from "@/lib/format-utils"

interface RecentlyViewedRailProps {
  /** The product pool from `getPopularProducts(...)` or similar. The
   *  RecentlyViewedCarousel filters this client-side against the visitor's
   *  localStorage entry and renders only the matching products. */
  seed: RecommendedProduct[]
}

const formatUSD = (value?: number) =>
  typeof value === "number" && value > 0
    ? formatPrice(value)
    : ""

function toCarouselProduct(p: RecommendedProduct): CarouselProduct {
  const hasDiscount =
    typeof p.originalPrice === "number" &&
    typeof p.price === "number" &&
    p.originalPrice > p.price
  return {
    brand: p.brand,
    title: p.title,
    price: formatUSD(p.price),
    originalPrice: hasDiscount ? formatUSD(p.originalPrice) : undefined,
    badge: hasDiscount ? "Price Drop" : "Recently viewed",
    badgeColor: hasDiscount ? "bg-red-500" : "bg-[#374151]",
    image: p.images?.[0] || "/placeholder.svg",
    href: p.canonical_url || `/products/${p.id}`,
    rating: p.rating,
    reviews: p.reviews,
    // Canonical badge set — this is the recently-viewed rail, so every product
    // also carries the client-side `seen_before` overlay (spec §4.3).
    badges: badgesFromRecommended(p, { seenBefore: true }),
  }
}

/**
 * RecentlyViewedRail — drop-in wrapper that takes a server-fetched product
 * pool and renders the standard "Recently viewed" carousel.
 *
 * Use this on any page that wants to surface a visitor's browsing history.
 * The underlying `RecentlyViewedCarousel` is localStorage-gated: it returns
 * `null` for fresh visitors (no IDs in storage), so dropping this on every
 * page type doesn't pollute layouts for first-time visitors.
 *
 * Phase B addition (2026-05-05): added to all `/local/*`, `/rebates/*`, and
 * `/brands/*` pages so the rail follows visitors across every page type.
 */
export function RecentlyViewedRail({ seed }: RecentlyViewedRailProps) {
  if (!seed || seed.length === 0) return null
  return <RecentlyViewedCarousel pool={toRecentlyViewedPool(seed)} />
}

/**
 * The exact client prop the carousel receives: one CarouselProduct per seed
 * row and nothing from the RecommendedProduct row that the card does not
 * print (no specs_json, no description, no image list beyond the lead).
 */
export function toRecentlyViewedPool(seed: readonly RecommendedProduct[]): RecentlyViewedItem[] {
  return seed.map((p) => ({ id: p.id, product: toCarouselProduct(p) }))
}
